import { useEffect, useMemo } from 'react';
import { toast } from 'sonner';

import { useWorkspace } from '@/features/workspaces/hooks/useWorkspaces';
import { CreateTaskDialog } from '../components/CreateTaskDialog';
import { DraftChip } from './DraftChip';
import { MissingBoardDialog } from './MissingBoardDialog';
import { isPending } from './draftUploads';
import { useTaskDraftStore } from './taskDraftStore';
import { useBeforeUnloadGuard } from '@/shared/hooks/useBeforeUnloadGuard';

/**
 * The drafts of one workspace, on every page of it (spec 121): a chip per
 * draft, bottom-right, and the one draft expanded as the Create Task dialog.
 *
 * Mounted by the workspace layout rather than the board, so a dialog folded
 * away on the board can be finished from the workflow's settings — and its
 * uploads, which live in the store, keep going in between (FR-005).
 */
export function TaskDraftDock({ workspaceId }: { workspaceId: string }) {
  const { data: workspace } = useWorkspace(workspaceId);
  const allDrafts = useTaskDraftStore((s) => s.drafts);
  const queues = useTaskDraftStore((s) => s.queues);
  const expandedKey = useTaskDraftStore((s) => s.expandedKey);
  const minimizedCount = useTaskDraftStore((s) => s.minimizedCount);
  const expand = useTaskDraftStore((s) => s.expand);
  const minimize = useTaskDraftStore((s) => s.minimize);
  const discardDraft = useTaskDraftStore((s) => s.discardDraft);
  const restoreDiscarded = useTaskDraftStore((s) => s.restoreDiscarded);
  const clearDraft = useTaskDraftStore((s) => s.clearDraft);

  // This workspace's only (FR-007), newest on top.
  const drafts = useMemo(
    () =>
      Object.entries(allDrafts)
        .filter(([, draft]) => draft.workspaceId === workspaceId)
        .sort(([, a], [, b]) => b.updatedAt.localeCompare(a.updatedAt)),
    [allDrafts, workspaceId],
  );

  // Leaving would lose files staged or on their way, in any queue — a draft's
  // or a task drawer's (FR-010). Text alone survives, so it does not ask.
  useBeforeUnloadGuard(
    Object.values(queues).some((queue) => queue.items.some(isPending)),
  );

  // A created task's draft stays only while its files are on their way. Once
  // they have all landed, folded away, there is nothing left for it to say.
  useEffect(() => {
    for (const [key, draft] of drafts) {
      if (draft.createdTaskId === null || key === expandedKey) continue;
      const items = queues[key]?.items ?? [];
      const unsettled = items.some(
        (item) => isPending(item) || item.status.kind === 'failed',
      );
      if (!unsettled) clearDraft(key);
    }
  }, [clearDraft, drafts, expandedKey, queues]);

  const discard = (key: string) => {
    const snapshot = discardDraft(key);
    if (!snapshot) return;
    toast('Draft discarded', {
      duration: 5000,
      action: {
        label: 'Undo',
        onClick: () => {
          restoreDiscarded(snapshot);
        },
      },
    });
  };

  const boardName = (workflowId: string) => {
    // Unknown until the workspace is read: not yet a deleted board.
    if (!workspace) return undefined;
    return workspace.workflows.find((w) => w.id === workflowId)?.name ?? null;
  };

  const expanded =
    expandedKey !== null && allDrafts[expandedKey]?.workspaceId === workspaceId
      ? allDrafts[expandedKey]
      : undefined;
  // Nothing is shown until the workspace is read: before then a board cannot
  // be told apart from a deleted one.
  const expandedWorkflow =
    expanded && workspace
      ? (workspace.workflows.find((w) => w.id === expanded.workflowId) ?? null)
      : undefined;

  if (drafts.length === 0) return null;
  return (
    <>
      <aside
        aria-label="Task drafts"
        className="pointer-events-none fixed right-4 bottom-4 z-40 flex flex-col items-end gap-2"
      >
        {drafts.map(([key, draft]) => (
          <DraftChip
            key={key}
            draft={draft}
            boardName={boardName(draft.workflowId)}
            items={queues[key]?.items ?? []}
            hidden={key === expandedKey && expandedWorkflow !== undefined}
            onOpen={() => {
              expand(key);
            }}
            onDiscard={() => {
              discard(key);
            }}
          />
        ))}
        <p role="status" aria-live="polite" className="sr-only">
          {/* Keyed so the same words are announced again on the next fold. */}
          {minimizedCount > 0 ? (
            <span key={minimizedCount}>Draft saved</span>
          ) : null}
        </p>
      </aside>
      {expandedKey !== null && expanded && expandedWorkflow === null ? (
        <MissingBoardDialog
          draft={expanded}
          onClose={() => {
            minimize(expandedKey);
          }}
          onDiscard={() => {
            discard(expandedKey);
          }}
        />
      ) : null}
      {expandedKey !== null && expandedWorkflow && workspace ? (
        <CreateTaskDialog
          key={expandedKey}
          draftKey={expandedKey}
          workspaceId={workspaceId}
          workflow={expandedWorkflow}
          labelRegistry={workspace.labels}
          onDiscard={() => {
            discard(expandedKey);
          }}
        />
      ) : null}
    </>
  );
}
